import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ApiService, RequestListItem } from '../../../shared/services/api.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { interval, Subscription } from 'rxjs';
import { ConsentFlowService } from '../../../shared/services/consent-flow.service';

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.css']
})
export class HistoryComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['customerName', 'searchCriteria', 'status', 'createdAt', 'availability', 'actions'];
  dataSource = new MatTableDataSource<RequestListItem>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  pollingSubscription: Subscription | null = null;
  consentRequired: boolean = true;
  isCheckingConsent = false;
  isLoadingRequests = false;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private snackBar: MatSnackBar,
    private consentFlowService: ConsentFlowService
  ) {}

  ngOnInit(): void {
    void this.ensureConsentThenLoad();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  get isLoadingHistory(): boolean {
    return this.isCheckingConsent || this.isLoadingRequests;
  }

  get loadingMessage(): string {
    return 'Loading requests...';
  }

  get hasRequests(): boolean {
    return this.dataSource.data.length > 0;
  }

  loadRequests(showLoader: boolean = true): void {
    if (showLoader) {
      this.isLoadingRequests = true;
    }
    this.apiService.listRequests().subscribe({
      next: (response) => {
        // Keep original timestamps - formatDate will be called in the template
        this.dataSource.data = response.requests;
        setTimeout(() => {
          if (this.paginator) {
            this.dataSource.paginator = this.paginator;
          }
          if (this.sort) {
            this.dataSource.sort = this.sort;
          }
        });
        this.startPollingForInProgress();
        this.isLoadingRequests = false;
      },
      error: (error) => {
        console.error('Failed to load requests:', error);
        let errorMessage = 'Failed to load requests';
        if (error.error?.error?.message) {
          errorMessage = error.error.error.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        this.isLoadingRequests = false;
      }
    });
  }

  private async ensureConsentThenLoad(): Promise<void> {
    this.isCheckingConsent = true;
    try {
      const consentAccepted = await this.consentFlowService.ensureConsentAccepted();
      this.consentRequired = !consentAccepted;
      if (!consentAccepted) {
        this.dataSource.data = [];
        return;
      }

      this.loadRequests();
    } catch (error) {
      console.error('Failed to validate consent status:', error);
      this.snackBar.open('Failed to validate POPIA acknowledgement state', 'Close', { duration: 5000 });
    } finally {
      this.isCheckingConsent = false;
    }
  }

  formatDate(timestamp: any): string {
    if (!timestamp) return '';
    
    // If it's already a formatted string (DD/MM/YYYY HH:MM:SS), return it as-is
    if (typeof timestamp === 'string' && /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(timestamp)) {
      return timestamp;
    }
    
    let date: Date;
    
    // Handle timestamp-like objects with toDate() helper
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    }
    // Handle objects with seconds/nanoseconds fields
    else if (timestamp.seconds !== undefined || timestamp._seconds !== undefined) {
      const seconds = timestamp.seconds || timestamp._seconds || 0;
      const nanoseconds = timestamp.nanoseconds || timestamp._nanoseconds || 0;
      date = new Date(seconds * 1000 + nanoseconds / 1000000);
    }
    // Handle plain Date object
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    // Handle ISO string
    else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    }
    // Handle number (milliseconds)
    else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    }
    // Fallback
    else {
      // Don't log warning for already-formatted strings
      if (typeof timestamp !== 'string' || !/^\d{2}\/\d{2}\/\d{4}/.test(timestamp)) {
        console.warn('Unknown timestamp format:', timestamp);
      }
      return '';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      // Don't log warning for already-formatted strings
      if (typeof timestamp !== 'string' || !/^\d{2}\/\d{2}\/\d{4}/.test(timestamp)) {
        console.warn('Invalid date:', timestamp);
      }
      return '';
    }
    
    // Format as day/month/year hour:minute:second in Africa/Johannesburg timezone
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    // Intl.DateTimeFormat with 'en-GB' gives DD/MM/YYYY format
    const parts = formatter.formatToParts(date);
    const day = parts.find(p => p.type === 'day')?.value || '';
    const month = parts.find(p => p.type === 'month')?.value || '';
    const year = parts.find(p => p.type === 'year')?.value || '';
    const hour = parts.find(p => p.type === 'hour')?.value || '';
    const minute = parts.find(p => p.type === 'minute')?.value || '';
    const second = parts.find(p => p.type === 'second')?.value || '';
    
    return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
  }

  private formatIdAs454(value: string): string {
    const compact = value.replace(/\s+/g, '');
    if (compact.length !== 13) return value;
    return `${compact.slice(0, 4)} ${compact.slice(4, 9)} ${compact.slice(9, 13)}`;
  }

  getSearchCriteria(request: RequestListItem): string {
    if (request.requestType === 'MULTI_SEARCH') {
      const total = request.multiSearch?.total ?? 0;
      return `Multi Search (${total} ID${total === 1 ? '' : 's'})`;
    }
    if (request.lookupDisplay && request.lookupType) {
      if (request.lookupType === 'ID') {
        return `ID: ${this.formatIdAs454(request.lookupDisplay)}`;
      }
      if (request.lookupType === 'TRUST') {
        return `Trust: ${request.lookupDisplay}`;
      }
      return `Address: ${request.lookupDisplay}`;
    }
    if (request.idNumber) {
      return `ID: ${this.formatIdAs454(request.idNumber)}`;
    } else if (request.trustNumber) {
      return `Trust: ${request.trustNumber}`;
    } else if (request.streetAddress) {
      return `Address: ${request.streetAddress}`;
    }
    return 'N/A';
  }

  getCustomerName(request: RequestListItem): string {
    const name = (request.customerName || '').trim();
    return name ? name : ' — ';
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'IN_PROGRESS':
        return 'hourglass_empty';
      case 'QUEUED':
        return 'queue';
      case 'FAILED':
        return 'error';
      case 'COMPLETE':
        return 'check_circle';
      case 'CANCELLED':
        return 'cancel';
      default:
        return 'help';
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'IN_PROGRESS':
        return 'accent';
      case 'QUEUED':
        return 'accent';
      case 'FAILED':
        return 'warn';
      case 'COMPLETE':
        return 'primary';
      case 'CANCELLED':
        return '';
      default:
        return '';
    }
  }

  isMultiSearch(request: RequestListItem): boolean {
    return request.requestType === 'MULTI_SEARCH' && !!request.multiSearch;
  }

  getStatusLabel(request: RequestListItem): string {
    if (this.isMultiSearch(request)) {
      return 'MULTI SEARCH';
    }
    return this.formatStatusLabel(request.status);
  }

  formatStatusLabel(status: string): string {
    return status.replace(/_/g, ' ');
  }

  getAvailabilityIcon(request: RequestListItem): string {
    const mode = request.viewAvailability?.mode;
    if (mode === 'NO_MATCH') return 'search_off';
    if (mode === 'FREE_UNTIL' || mode === 'FREE') return 'verified';
    if (mode === 'CREDIT_COST') return 'paid';
    if (mode === 'FAILED') return 'error';
    if (mode === 'INSUFFICIENT_CREDITS') return 'money_off';
    if (mode === 'UNAVAILABLE') return 'block';
    return 'hourglass_empty';
  }

  getAvailabilityIconColor(request: RequestListItem): string {
    const mode = request.viewAvailability?.mode;
    if (mode === 'FREE_UNTIL' || mode === 'FREE') return 'primary';
    if (mode === 'CREDIT_COST') return 'warn';
    return '';
  }

  getAvailabilityText(request: RequestListItem): string {
    if (this.isMultiSearch(request)) return '—';
    const availability = request.viewAvailability;
    if (!availability) return '—';
    switch (availability.mode) {
      case 'FREE':
        return 'Free view';
      case 'FREE_UNTIL':
        return `Free until ${this.formatFreeUntilLabel(availability.freeUntil || null)}`;
      case 'CREDIT_COST':
        return this.formatCreditCostLabel(availability.creditCost);
      case 'NO_MATCH':
        return 'No result found';
      case 'UNAVAILABLE':
        return 'Not available';
      case 'INSUFFICIENT_CREDITS':
        return 'Not available - no credits';
      case 'FAILED':
        return 'Failed';
      case 'NOT_READY':
      default:
        return 'Available when complete';
    }
  }

  getAvailabilityTooltip(request: RequestListItem): string {
    if (this.isMultiSearch(request)) return 'Open multi search to monitor individual requests';
    const availability = request.viewAvailability;
    if (!availability) return 'Availability unknown';
    switch (availability.mode) {
      case 'FREE':
        return 'Viewing this entry is free';
      case 'FREE_UNTIL': {
        const full = this.formatDate(availability.freeUntil);
        return full ? `Free view active until ${full}` : 'Free view active';
      }
      case 'CREDIT_COST':
        return `Viewing this entry will incur a cost (${this.formatCreditCostLabel(availability.creditCost)})`;
      case 'NO_MATCH':
        return 'No result found for this ID number.';
      case 'UNAVAILABLE':
        return 'This request is unavailable due to Do Not Contact status';
      case 'INSUFFICIENT_CREDITS':
        return 'This request completed when your credit balance was zero. Buy credits and run a new search.';
      case 'FAILED':
        return 'This request failed. Please run a new search.';
      case 'NOT_READY':
      default:
        return 'This request can be viewed once processing is complete';
    }
  }

  private formatFreeUntilLabel(value: string | null): string {
    if (!value) return 'soon';
    const date = new Date(value);
    if (isNaN(date.getTime())) return 'soon';

    const time = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);

    const dayKey = this.dayKeyInJohannesburg(date);
    const now = new Date();
    const todayKey = this.dayKeyInJohannesburg(now);
    const tomorrowKey = this.dayKeyInJohannesburg(new Date(now.getTime() + 24 * 60 * 60 * 1000));
    if (dayKey === todayKey) return `${time} today`;
    if (dayKey === tomorrowKey) return `${time} tomorrow`;
    return `${time} on ${new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      day: '2-digit',
      month: '2-digit',
    }).format(date)}`;
  }

  private formatCreditCostLabel(value: number | null | undefined): string {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return 'credit cost unavailable';
    }
    const normalized = Math.max(1, Math.trunc(parsed));
    return `${normalized} credit${normalized === 1 ? '' : 's'}`;
  }

  private dayKeyInJohannesburg(date: Date): string {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }

  viewData(request: RequestListItem): void {
    if (this.consentRequired) {
      this.snackBar.open('Please accept the POPIA acknowledgement before viewing data', 'Close', { duration: 5000 });
      return;
    }
    if (request.lookupType === 'TRUST') {
      void this.router.navigate(['/admin/trust-data', request.requestId]);
      return;
    }
    void this.router.navigate(['/admin/data', request.requestId]);
  }

  canViewRequest(request: RequestListItem): boolean {
    if (this.isMultiSearch(request)) return false;
    if (request.status !== 'COMPLETE') return false;
    if (this.consentRequired) return false;
    if (request.dncBlocked === true) return false;
    if (request.viewAvailability?.mode === 'UNAVAILABLE') return false;
    if (request.viewAvailability?.mode === 'INSUFFICIENT_CREDITS') return false;
    if (request.viewAvailability?.mode === 'NO_MATCH') return false;
    if (request.viewAvailability?.mode === 'NOT_READY') return false;
    return true;
  }

  getViewDisabledReason(request: RequestListItem): string {
    if (this.isMultiSearch(request)) return '';
    if (this.consentRequired) {
      return 'Please accept the POPIA acknowledgement before viewing data.';
    }
    if (request.dncBlocked === true || request.viewAvailability?.mode === 'UNAVAILABLE') {
      return 'This request is unavailable due to Do Not Contact status';
    }
    if (request.viewAvailability?.mode === 'INSUFFICIENT_CREDITS') {
      return 'This request is unavailable because you had no credits when it completed.';
    }
    if (request.viewAvailability?.mode === 'NO_MATCH') {
      return 'No result found for this ID number.';
    }
    if (request.viewAvailability?.mode === 'FAILED' || request.status === 'FAILED') {
      return 'This request failed. Please run a new search.';
    }
    if (request.viewAvailability?.mode === 'NOT_READY' || request.status !== 'COMPLETE') {
      return 'This request can be viewed once processing is complete';
    }
    return '';
  }

  openMultiSearch(request: RequestListItem): void {
    const batchId = request.multiSearch?.requestBatchId;
    if (!batchId) return;
    void this.router.navigate(['/admin/multi-search', batchId]);
  }

  cancelRequest(requestId: string): void {
    if (confirm('Are you sure you want to cancel this request?')) {
      this.apiService.cancelRequest(requestId).subscribe({
        next: () => {
          this.snackBar.open('Request cancelled', 'Close', { duration: 3000 });
          this.loadRequests();
        },
        error: (error) => {
          this.snackBar.open('Failed to cancel request', 'Close', { duration: 3000 });
        }
      });
    }
  }

  startPollingForInProgress(): void {
    const hasActiveRequests = this.dataSource.data.some(
      (req) => req.status === 'IN_PROGRESS' || req.status === 'QUEUED'
    );
    if (!hasActiveRequests) {
      this.stopPolling();
      return;
    }
    if (this.pollingSubscription) return;
    this.pollingSubscription = interval(4000).subscribe(() => this.loadRequests(false));
  }

  stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }
}
